/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { Images } from '../../common';
import AWSLogoSVG from '../../images/awsLogo.svg';
import MinIOLogoSVG from '../../images/minioLogo.svg';
import './settings.css';

function StorageCard({ value, selected, onSelect, logo, logoAlt, description, children }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className="relative text-center rounded-[12px] border p-5 transition bg-white cursor-pointer"
      style={{
        flex: '1 1 220px',
        minWidth: 220,
        borderStyle: 'solid',
        borderColor: selected ? '#3d5a8a' : '#e0e1e6',
        background: selected ? '#f5f7fd' : '#fff',
        boxShadow: selected ? '0 0 0 1px #3d5a8a' : 'none',
      }}
    >
      <span
        className="absolute flex items-center justify-center"
        style={{ top: 12, right: 12, width: 18, height: 18, borderRadius: 9, border: `2px solid ${selected ? '#3d5a8a' : '#c7cbd6'}` }}
      >
        {selected ? <span style={{ width: 8, height: 8, borderRadius: 4, background: '#3d5a8a' }} /> : null}
      </span>
      <img src={logo} width="56" height="56" alt={logoAlt} style={{ objectFit: 'contain', margin: '4px auto 16px', display: 'block' }} />
      <p className="text-sm text-ink-500" style={{ margin: 0 }}>{description}</p>
      {children}
    </button>
  );
}

function Field({ label, name, value, onChange, type = 'text', placeholder, helper, autoComplete = 'off' }) {
  return (
    <div className="mb-4">
      <span className="block text-sm font-medium text-ink-900 mb-1.5">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        autoComplete={autoComplete}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full box-border h-10 px-3 border border-rule rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-brand-500"
        style={{ borderStyle: 'solid' }}
      />
      {helper ? <p className="text-xs text-ink-500 mt-1.5">{helper}</p> : null}
    </div>
  );
}

function DataStorage() {
  const { activeWorkspace } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dataRetentionDays, setDataRetentionDays] = useState(null);
  const [storageSettings, setStorageSettings] = useState({
    storageOption: 'Shoot', awsBucketName: '', awsAccessKeyId: '', awsSecretAccessKey: '', minioEndpoint: '',
  });
  const { shootLogo } = Images;

  useEffect(() => {
    const fetchStorageSettings = async () => {
      try {
        setLoading(true);
        const response = await sendRequest({ method: 'get', url: API.ORGANIZATION.getStorageSettings(activeWorkspace.organizationId) });
        if (response.success) {
          setStorageSettings({
            storageOption: response.data?.data?.storageOption || 'Shoot',
            awsBucketName: response.data?.data?.awsBucketName || '',
            awsAccessKeyId: response.data?.data?.awsAccessKeyId || '',
            awsSecretAccessKey: '',
            minioEndpoint: response.data?.data?.minioEndpoint || '',
          });
        }
        const featuresResponse = await sendRequest({ method: 'get', url: API.WORKSPACE.features(activeWorkspace.id) });
        if (featuresResponse.success && featuresResponse.data?.features) {
          const retentionDays = featuresResponse.data.features['Data Retention'];
          if (retentionDays) setDataRetentionDays(retentionDays);
        }
      } catch (err) {
        setError('Failed to load storage settings');
      } finally {
        setLoading(false);
      }
    };
    if (activeWorkspace?.organizationId && activeWorkspace?.id) fetchStorageSettings();
  }, [activeWorkspace?.organizationId, activeWorkspace?.id]);

  const onField = (e) => setStorageSettings({ ...storageSettings, [e.target.name]: e.target.value });
  const selectOption = (value) => setStorageSettings({ ...storageSettings, storageOption: value });

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (storageSettings.storageOption === 'AWS') {
      if (!storageSettings.awsBucketName || !storageSettings.awsAccessKeyId) {
        setError('Please fill in all required AWS fields');
        return;
      }
    }
    if (storageSettings.storageOption === 'MinIO') {
      if (!storageSettings.awsBucketName || !storageSettings.awsAccessKeyId || !storageSettings.minioEndpoint) {
        setError('Please fill in all required MinIO fields');
        return;
      }
    }
    try {
      setSaving(true);
      const response = await sendRequest({ method: 'put', url: API.ORGANIZATION.updateStorageSettings(activeWorkspace.organizationId), data: storageSettings });
      if (response.success) {
        setSuccess('Storage settings updated successfully');
        setStorageSettings((prev) => ({ ...prev, awsSecretAccessKey: '', minioEndpoint: response.data.minioEndpoint || prev.minioEndpoint }));
      } else {
        setError(response.message || 'Failed to update storage settings');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update storage settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-ink-500 py-6 text-center">Loading…</p>;

  const retentionLabel = dataRetentionDays ? `${dataRetentionDays} day${dataRetentionDays !== 1 ? 's' : ''}` : null;

  return (
    <div>
      <h4 className="text-base font-semibold text-ink-900 mb-1">Data Storage Settings</h4>
      <p className="text-sm text-ink-500 mb-4">
        Choose where your data will be stored. Screenshots and webcam data will be saved to your selected storage provider.
      </p>

      {error && <div className="text-sm rounded-[8px] px-3 py-2 mb-3" style={{ background: '#fdebe6', color: '#a8391d' }}>{error}</div>}
      {success && <div className="text-sm rounded-[8px] px-3 py-2 mb-3" style={{ background: '#eef5ea', color: '#458a44' }}>{success}</div>}

      <span className="block text-sm font-medium text-ink-900 mb-2">Storage Provider</span>
      <div className="flex flex-wrap gap-4 mb-5">
        <StorageCard value="Shoot" selected={storageSettings.storageOption === 'Shoot'} onSelect={selectOption} logo={shootLogo} logoAlt="Shoot" description="Shoot can store your data on your behalf.">
          {retentionLabel && <p className="text-xs text-ink-500 font-medium mt-2" style={{ marginBottom: 0 }}>{`Data Retention: ${retentionLabel}`}</p>}
        </StorageCard>
        <StorageCard value="AWS" selected={storageSettings.storageOption === 'AWS'} onSelect={selectOption} logo={AWSLogoSVG} logoAlt="AWS" description="Use AWS to store your own data." />
        <StorageCard value="MinIO" selected={storageSettings.storageOption === 'MinIO'} onSelect={selectOption} logo={MinIOLogoSVG} logoAlt="MinIO" description="Use MinIO for On-Premise storage." />
      </div>

      {storageSettings.storageOption === 'Shoot' && retentionLabel && (
        <div className="rounded-[8px] p-3 mb-5" style={{ background: '#e8eef7', maxWidth: 720 }}>
          <p className="text-sm font-semibold text-ink-900 mb-0.5">Shoot Managed Storage</p>
          <p className="text-sm text-ink-700">
            {'Your data is securely stored by Shoot with a retention period of '}
            <strong>{retentionLabel}</strong>
            . Screenshots and webcam data are automatically deleted after this period.
          </p>
        </div>
      )}

      {storageSettings.storageOption === 'AWS' && (
        <div className="mb-5" style={{ maxWidth: 560 }}>
          <span className="block text-sm font-medium text-ink-900 mb-2">AWS Configuration</span>
          <Link to="/organization-signup/aws-setup" target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-brand-700 hover:underline mb-4">
            Follow our guide to set up your AWS data storage →
          </Link>
          <Field label="AWS S3 Bucket Name" name="awsBucketName" value={storageSettings.awsBucketName} onChange={onField} placeholder="Enter your S3 bucket name" />
          <Field label="AWS Access Key ID" name="awsAccessKeyId" value={storageSettings.awsAccessKeyId} onChange={onField} placeholder="Enter your AWS Access Key ID" />
          <Field label="AWS Secret Access Key" name="awsSecretAccessKey" type="password" autoComplete="new-password" value={storageSettings.awsSecretAccessKey} onChange={onField} placeholder="Enter your AWS Secret Access Key" helper="Leave empty to keep your current secret key" />
        </div>
      )}

      {storageSettings.storageOption === 'MinIO' && (
        <div className="mb-5" style={{ maxWidth: 560 }}>
          <span className="block text-sm font-medium text-ink-900 mb-2">MinIO Configuration</span>
          <Field label="MinIO Endpoint URL" name="minioEndpoint" value={storageSettings.minioEndpoint} onChange={onField} placeholder="https://minio.example.com" helper="Enter the MinIO server endpoint URL" />
          <Field label="Bucket Name" name="awsBucketName" value={storageSettings.awsBucketName} onChange={onField} placeholder="Enter your bucket name" />
          <Field label="Access Key ID" name="awsAccessKeyId" value={storageSettings.awsAccessKeyId} onChange={onField} placeholder="Enter your Access Key ID" />
          <Field label="Secret Access Key" name="awsSecretAccessKey" type="password" autoComplete="new-password" value={storageSettings.awsSecretAccessKey} onChange={onField} placeholder="Enter your Secret Access Key" helper="Leave empty to keep your current secret key" />
        </div>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center justify-center box-border h-10 px-6 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </div>
  );
}

export default DataStorage;
